import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import ChatIcon from '@mui/icons-material/Chat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { usersApi, getStatusDisplay, getTypeDisplay } from '../services/api';

// Format timestamp
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Format date only
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Safely render a value that might be an object
const safeString = (val) => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    if (val.text) return String(val.text);
    if (val.message) return String(val.message);
    if (val.body) return String(val.body);
    try { return JSON.stringify(val); } catch { return '[Object]'; }
  }
  return String(val);
};

// Message bubble for timeline view (compact version)
const TimelineMessage = ({ message }) => {
  if (!message) return null;

  const isSystem = message.sender_type === 'SYSTEM';
  const isNutritionist = message.sender_type === 'NUTRITIONIST';

  let bgcolor = '#f5f5f5';
  let textColor = 'text.primary';

  if (isNutritionist) {
    bgcolor = '#E8F5E9';
  } else if (message.sender_type === 'USER') {
    bgcolor = '#E3F2FD';
  } else if (isSystem) {
    bgcolor = '#FFF3E0';
    textColor = 'text.secondary';
  }

  const bodyText = safeString(message.body);
  const subjectText = safeString(message.subject);
  const senderDisplay = safeString(message.sender_display) ||
    (isSystem ? 'System' : isNutritionist ? 'You' : 'User');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isNutritionist ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '80%',
          bgcolor,
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" fontWeight={600} color={textColor}>
            {senderDisplay}
            {message.message_type && ` - ${typeof message.message_type === 'string' ? message.message_type.replace(/_/g, ' ') : safeString(message.message_type)}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" ml={2} sx={{ whiteSpace: 'nowrap' }}>
            {formatTime(message.created_at)}
          </Typography>
        </Box>

        {subjectText && (
          <Typography variant="subtitle2" fontWeight={600} mb={0.5} sx={{ fontSize: '0.8rem' }}>
            {subjectText}
          </Typography>
        )}

        <Typography
          variant="body2"
          color={textColor}
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.825rem' }}
        >
          {bodyText}
        </Typography>
      </Box>
    </Box>
  );
};

// Thread card in the timeline
const ThreadBlock = ({ thread, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const statusDisplay = getStatusDisplay(thread.thread_status);
  const typeDisplay = getTypeDisplay(thread.thread_type);
  const isClosed = thread.thread_status === 'CLOSED';

  return (
    <Card
      sx={{
        mb: 2,
        border: isClosed ? '1px solid #e0e0e0' : '1px solid #4CAF50',
        borderLeft: isClosed ? '4px solid #9e9e9e' : '4px solid #4CAF50',
      }}
    >
      <CardContent sx={{ pb: expanded ? 1 : '16px !important' }}>
        {/* Thread header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                label={typeDisplay.label}
                color={typeDisplay.color}
                size="small"
                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
              />
              <Chip
                label={statusDisplay.label}
                color={statusDisplay.color}
                size="small"
                variant="outlined"
                icon={isClosed ? <CheckCircleIcon /> : <PendingIcon />}
                sx={{ fontSize: '0.7rem' }}
              />
              {thread.thread_reference_id && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {thread.thread_reference_id}
                </Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={2} mt={1} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Created: {formatDate(thread.thread_created_at)}
              </Typography>
              {thread.thread_closed_at && (
                <Typography variant="caption" color="text.secondary">
                  Closed: {formatDate(thread.thread_closed_at)}
                </Typography>
              )}
              {thread.max_followups > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Follow-ups: {thread.followups_used}/{thread.max_followups}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            {thread.thread_reference_id && (
              <Tooltip title="Open thread">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/thread/${thread.thread_reference_id}`)}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={expanded ? 'Collapse messages' : 'Expand messages'}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Message count summary */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block', cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}
        >
          {thread.messages?.length || 0} message{(thread.messages?.length || 0) !== 1 ? 's' : ''}
          {!expanded && ' — click to expand'}
        </Typography>

        {/* Expandable messages */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
            {thread.messages?.map((msg, idx) => (
              <TimelineMessage key={msg.id || idx} message={msg} />
            ))}
            {(!thread.messages || thread.messages.length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>
                No messages in this thread.
              </Typography>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const UserTimeline = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTimeline = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await usersApi.getTimeline(userId);
      setTimelineData(data);
    } catch (err) {
      console.error('Failed to fetch timeline:', err);
      setError('Failed to load client timeline. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const user = timelineData?.user;
  const timeline = timelineData?.timeline || [];
  const threadCount = timelineData?.thread_count || 0;
  const openThreadCount = timelineData?.open_thread_count || 0;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate('/users')}>
            <ArrowBackIcon />
          </IconButton>
          <TimelineIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Client History
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditNoteIcon />}
            onClick={() => navigate(`/users/${userId}/create-checkin`)}
          >
            Create Check-in
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchTimeline} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && timelineData && (
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
          {/* User summary sidebar */}
          <Box sx={{ width: isMobile ? '100%' : 280, flexShrink: 0 }}>
            <Card sx={{ position: isMobile ? 'static' : 'sticky', top: 20 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Avatar sx={{ bgcolor: '#FBBC04', width: 44, height: 44 }}>
                    {(user?.first_name || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {user?.first_name || 'User'} {user?.last_name ? user.last_name.charAt(0) + '.' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {userId}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Stats */}
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Total Threads</Typography>
                    <Chip label={threadCount} size="small" />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Open</Typography>
                    <Chip
                      label={openThreadCount}
                      size="small"
                      color={openThreadCount > 0 ? 'success' : 'default'}
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Closed</Typography>
                    <Chip label={threadCount - openThreadCount} size="small" />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  startIcon={<EditNoteIcon />}
                  onClick={() => navigate(`/users/${userId}/create-checkin`)}
                  sx={{ textTransform: 'none' }}
                >
                  Create Check-in
                </Button>
              </CardContent>
            </Card>
          </Box>

          {/* Timeline content */}
          <Box flex={1} minWidth={0}>
            {timeline.length === 0 ? (
              <Card>
                <CardContent>
                  <Box textAlign="center" py={4}>
                    <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No conversations yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      This client has no message threads.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              timeline.map((thread, idx) => (
                <ThreadBlock key={thread.thread_reference_id || idx} thread={thread} navigate={navigate} />
              ))
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserTimeline;
